// The board's arrows, worked out without a board so the rule is tested: at
// every position of the game the board draws the green arrow and the played
// move's arrow; only an unsolved puzzle hides them (the board's
// `boardArrowsVisibility` constraint, applied by the `BoardModel`).
//
// The green arrow is, in order:
//   1. on the line of the game's opening variation (the review's book,
//      `GameBook.variationMoveFrom`): the line's next move, the main line's
//      while the two still share their moves, then the variation's own
//      (brush `book`);
//   2. the shortlist's deeper search of this position, when it has one
//      (it sends no best move while the theory list shows) (brush `best`);
//   3. the review's scan's best move for the game's position (brush `best`).
// The played move's arrow is the shortlist's when it read this position,
// else the scan's: `blunder` (red) for a blunder, `played` (grey) otherwise;
// when it is the green move there is one green arrow.
//
// `annotate` is the rule as the `BoardModel` takes it; ground.ts gives each
// brush its colour.
import type { Chess } from '@chess/api-chess';
import { positionAt, type ReviewState } from '@chess/api-review';
import type { Annotate, BoardArrow, BoardBrush, ShortlistReading } from '@chess/api-review/board';
import type { Uci } from '@chess/api-model';

export interface PlayedArrow {
  uci: Uci;
  brush: 'played' | 'blunder';
}

export interface GreenArrow {
  uci: Uci;
  brush: 'best' | 'book';
}

/** What the scan says about one game position: its best move and the move the game played from it. */
export interface ScanReading {
  best?: Uci;
  played?: PlayedArrow;
}

function arrow(uci: Uci, brush: BoardBrush): BoardArrow | undefined {
  if (uci.length < 4) return undefined;
  return { from: uci.slice(0, 2), to: uci.slice(2, 4), brush };
}

/** The played move first and the green one on top; when they are the same move, one green arrow. */
export function shapes(green: GreenArrow | undefined, played: PlayedArrow | undefined): BoardArrow[] {
  const out: BoardArrow[] = [];
  const p = played && played.uci !== green?.uci ? arrow(played.uci, played.brush) : undefined;
  if (p) out.push(p);
  const b = green ? arrow(green.uci, green.brush) : undefined;
  if (b) out.push(b);
  return out;
}

/**
 * The game ply of the position the board shows, or undefined on a line of
 * the reader's own, which the scan does not know. Looking back (`browse`)
 * shows an earlier position, so the scan is read there, not at the head.
 */
export function gamePlyOf(state: ReviewState): number | undefined {
  if (state.browse !== undefined) return state.exploring && state.browse > state.exploring.fromPly ? undefined : state.browse;
  return state.exploring ? undefined : state.ply;
}

/** The review's scan's reading of the game position on the board, graded under the user's rules. */
export function scanReading(state: ReviewState): ScanReading | undefined {
  const ply = gamePlyOf(state);
  const game = state.game;
  if (ply === undefined || !game) return undefined;
  const move = game.moves[ply];
  let played: PlayedArrow | undefined;
  if (move) {
    // The scan carries the user's rules; the move at ply + 1 is White's when the ply is even.
    const grade = state.scan?.grade(ply + 1, move, ply % 2 === 0 ? 'white' : 'black');
    played = { uci: move.uci, brush: grade === 'blunder' ? 'blunder' : 'played' };
  }
  return { best: state.scan?.position(ply)?.lines[0]?.uci, played };
}

export interface ArrowSources {
  /** The position on the board. */
  fen?: string;
  /** The variation line's next move from here, as UCI; undefined off the line. */
  line?: Uci;
  /** The shortlist's latest reading; used only when it is about `fen`. */
  shortlist: ShortlistReading;
  /** The scan's reading of this game position. */
  scan?: ScanReading;
}

/** The arrows for the position on the board. */
export function arrowsFor({ fen, line, shortlist, scan }: ArrowSources): BoardArrow[] {
  const live = fen && shortlist.fen === fen ? shortlist : undefined;
  const best = live?.best?.uci ?? scan?.best;
  const green: GreenArrow | undefined = line ? { uci: line, brush: 'book' } : best ? { uci: best, brush: 'best' } : undefined;
  const played: PlayedArrow | undefined = live?.played
    ? { uci: live.played.uci, brush: live.played.verdict === 'blunder' ? 'blunder' : 'played' }
    : scan?.played;
  return shapes(green, played);
}

/** The variation line's next move from a position, as UCI; undefined off the line. */
export function lineMoveOf(state: ReviewState, fen: string | undefined, chess: Pick<Chess, 'play'>): Uci | undefined {
  const san = fen ? state.book?.variationMoveFrom(fen) : undefined;
  return san && fen ? chess.play(fen, san)?.move.uci : undefined;
}

/** The board's arrows for a review state, as the `BoardModel` asks for them. */
export const annotate: Annotate = (state, shortlist, chess) => {
  const fen = positionAt(state);
  return arrowsFor({ fen, line: lineMoveOf(state, fen, chess), shortlist, scan: scanReading(state) });
};
